import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, Inject, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../shared/pagination-options';

export interface ParticipantDetail {
  loginId: string;
  isAdhoc: boolean;
  hasKey: boolean;
  alreadyRedeemed: boolean;
  redemptionDate: string | null;
  redeemedByEmail: string | null;
  crossAccountRedemption: boolean;
  userName: string | null;
}

@Component({
  selector: 'app-participant-details-tab',
  templateUrl: './participant-details-tab.component.html',
  styleUrls: ['./participant-details-tab.component.css'],
})
export class ParticipantDetailsTabComponent implements OnChanges {
  @Input() classId!: string;
  @Input() classEntity: ClassesEntity | null = null;
  /** Emitted from the empty-state buttons — parent navigates to Settings → Participants. */
  @Output() goToSettingsParticipants = new EventEmitter<void>();

  @ViewChild(MatPaginator) set paginator(p: MatPaginator) {
    if (p) this.tableSource.paginator = p;
  }

  tableSource = new MatTableDataSource<ParticipantDetail>([]);
  displayedColumns = ['loginId', 'type', 'redemptionStatus', 'redeemedBy', 'crossAccount'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;
  isLoading = false;
  loadError: string | null = null;
  /** Set when the API returns reason=CLASS_NOT_CONFIRMED. */
  notConfirmedMessage: string | null = null;

  private readonly logger = Log.create(ParticipantDetailsTabComponent);

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['classId'] && this.classId) {
      this.load();
    }
  }

  load(): void {
    if (!this.classId) return;
    this.isLoading = true;
    this.loadError = null;
    this.notConfirmedMessage = null;

    this.classService.getParticipantDetails(this.classId).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.reason) {
          this.notConfirmedMessage = res.message ?? 'Class not yet confirmed.';
          this.tableSource.data = [];
          this.logger.i('Participant details: class unconfirmed, reason:', res.reason);
        } else {
          this.tableSource.data = res.participants ?? [];
          this.logger.i('Participant details loaded:', this.tableSource.data.length);
        }
      },
      error: (err: any) => {
        this.loadError = err?.error?.message ?? 'Failed to load participant details.';
        this.isLoading = false;
        this.logger.e('Participant details load error:', err);
      },
    });
  }
}
